import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import type { FormEvent, ReactNode } from "react";
import { healthAt, type ChangeSummary, type HealthDto, type ThreadLive, type ThreadSummary } from "@nabla/api";
import type { Actions } from "./actions.ts";
import type { HeldChange } from "./changes.ts";
import type { Client } from "./client.ts";
import type { SessionState } from "./session.ts";
import { settledBetween, type CommandRecord, type Commands, type Notice, type Settled } from "./commands.ts";
import type { ClientError } from "./http.ts";
import { myList } from "./places.ts";
import type { Prefs, Theme } from "./prefs.ts";
import type { ViewRequest } from "./request.ts";
import type { Connection } from "./stream.ts";
import type { Progress } from "./threads.ts";
import type { ViewEntry } from "./views.ts";

interface Nabla {
  readonly client: Client;
  readonly actions: Actions;
}

const NablaContext = createContext<Nabla | null>(null);
export function ClientProvider({ client, actions, children }: { readonly client: Client; readonly actions: Actions; readonly children: ReactNode }) {
  const value = useMemo(() => ({ client, actions }), [client, actions]);
  return createElement(NablaContext.Provider, { value }, children);
}

function useNabla(): Nabla {
  const nabla = useContext(NablaContext);
  if (!nabla) throw new Error("a client hook ran outside ClientProvider");
  return nabla;
}

export function useClient(): Client {
  return useNabla().client;
}

export function useActions(): Actions {
  return useNabla().actions;
}

function useTopic<T>(topic: string, read: (client: Client) => T): T {
  const client = useClient();
  const subscribe = useCallback((listener: () => void) => client.hub.subscribe(topic, listener), [client, topic]);
  return useSyncExternalStore(subscribe, () => read(client));
}

export type ViewState<T> =
  | { readonly status: "carregando" }
  | { readonly status: "pronta"; readonly data: T; readonly stale: boolean; readonly refreshing: boolean; readonly error?: ClientError }
  | { readonly status: "erro"; readonly error: ClientError };
export function useView<T>(request: ViewRequest<T> | undefined): ViewState<T> {
  const entry = useHeld(request?.key);
  const read = request?.read;
  return useMemo(() => viewState(entry, read), [entry, read]);
}

export function useHeld(key: string | undefined): ViewEntry | undefined {
  const client = useClient();
  useEffect(() => (key === undefined ? undefined : client.views.watch(key)), [client, key]);
  return useTopic(`view:${key ?? ""}`, (c) => (key === undefined ? undefined : c.views.entry(key)));
}

export function viewState<T>(entry: ViewEntry | undefined, read: ViewRequest<T>["read"] | undefined): ViewState<T> {
  if (entry === undefined || read === undefined) return { status: "carregando" };
  if (entry.held === undefined) return entry.error === undefined ? { status: "carregando" } : { status: "erro", error: entry.error };
  const readout = read(entry.held);
  if (!readout.ok) return { status: "erro", error: { kind: "formato", detail: readout.detail } };
  return { status: "pronta", data: readout.value, stale: entry.stale, refreshing: entry.loading, ...(entry.error === undefined ? {} : { error: entry.error }) };
}

export function useRefetch(key: string | undefined): () => void {
  const client = useClient();
  return useCallback(() => {
    if (key !== undefined) void client.views.refetch(key);
  }, [client, key]);
}

export function useChange(summary: ChangeSummary): { readonly change: ChangeSummary; readonly arrivedAt?: number } {
  const held: HeldChange | undefined = useTopic(`change:${summary.id}`, (c) => c.change(summary.id));
  if (held && Date.parse(held.change.updatedAt) >= Date.parse(summary.updatedAt) && held.change !== summary) return held;
  return { change: summary };
}

export function useThread(id: string): ThreadSummary | undefined {
  return useTopic(`thread:${id}`, (c) => c.thread(id));
}

export function useProgress(id: string): Progress | undefined {
  return useTopic(`thread:${id}`, (c) => c.progress(id));
}

export function useLive(id: string): ThreadLive | undefined {
  return useTopic(`thread:${id}`, (c) => c.live(id));
}

export function useThreads(): readonly ThreadSummary[] {
  return useTopic("threads", (c) => c.threads());
}

export function useMyThreads(): readonly ThreadSummary[] {
  useHeld(myList);
  return useTopic("threads", (c) => c.mine());
}

type News = (thread: ThreadSummary) => ThreadSummary["news"];

export function useSeenAnswers(): { readonly news: News } {
  const client = useClient();
  const held = useTopic("threads", (c) => c.threads());
  return useMemo(() => ({ news: (thread: ThreadSummary) => client.news(thread) }), [client, held]);
}

export function useHealth(fallback?: HealthDto): HealthDto | undefined {
  const held = useTopic("health", (c) => c.health());
  const now = useNow();
  return useMemo(() => held ?? (fallback && healthAt(fallback, now)), [held, fallback, now]);
}

export function useConnection(): { readonly connection: Connection; readonly offline: boolean } {
  const connection = useTopic("connection", (c) => c.connection());
  const offline = useTopic("connection", (c) => c.offline());
  return { connection, offline };
}

export function useSession(): SessionState {
  return useTopic("session", (c) => c.session());
}

export function useCommand(target: string): (CommandRecord & { readonly retry: () => void }) | undefined {
  const client = useClient();
  const record = useTopic(`command:${target}`, (c) => c.commands.latest(target));
  return useMemo(() => (record ? { ...record, retry: () => client.commands.retry(record.requestKey) } : undefined), [client, record]);
}

export function useSettled(target: string, on: (settled: Settled) => void): void {
  const record = useTopic(`command:${target}`, (c) => c.commands.latest(target));
  const before = useRef(record);
  useEffect(() => {
    const settled = settledBetween(before.current, record);
    before.current = record;
    if (settled !== undefined) on(settled);
  }, [record]);
}

export function useSheetDraft<D>(target: string, initial: D, onClose: () => void) {
  const [draft, setDraft] = useState<D>(initial);
  const [tried, setTried] = useState(false);
  const sending = useCommand(target)?.state === "enviando";
  useSettled(target, (settled) => settled.state === "registrada" && onClose());
  const set = (part: Partial<D>) => setDraft({ ...draft, ...part });
  const tries = (event: FormEvent) => {
    event.preventDefault();
    setTried(true);
    return !sending;
  };
  return { draft, set, tried, sending, tries };
}

export function useNotice(): { readonly notice: Notice | undefined;
  readonly dismiss: (id: number) => void; readonly tell: Commands["tell"] } {
  const client = useClient();
  const notice = useTopic("notices", (c) => c.commands.notice());
  return { notice, dismiss: client.commands.dismiss, tell: client.commands.tell };
}

export function useNow(period = 30_000): number {
  const client = useClient();
  const [now, setNow] = useState(client.now);
  useEffect(() => {
    const id = setInterval(() => setNow(client.now()), period);
    return () => clearInterval(id);
  }, [client, period]);
  return now;
}

function usePref<T>(topic: string, pref: Pref<T>): readonly [T, (next: T) => void] {
  const client = useClient();
  const value = useTopic(topic, (c) => pref.read(c.prefs));
  const set = useCallback(
    (next: T) => {
      pref.write(client.prefs, next);
      client.hub.touch(topic);
    },
    [client, topic, pref],
  );
  return [value, set];
}

type Pref<T> = { readonly read: (prefs: Prefs) => T; readonly write: (prefs: Prefs, next: T) => void };
const theme: Pref<Theme> = { read: (prefs) => prefs.theme(), write: (prefs, next) => prefs.setTheme(next) };

export const useTheme = () => usePref("theme", theme);

export function useReloadOffered(): boolean {
  return useTopic("reload", (c) => c.reloadOffered());
}
